type SerialisablePrimitive = null | undefined | string | number | boolean
export type SerialisableParams = Record<string, SerialisablePrimitive>
export type SerialisableBody = {
  [key: string]:
    | SerialisablePrimitive
    | SerialisableBody
    | Array<SerialisablePrimitive | SerialisableBody>
}

// What a request carries: serialisable data, and files, which travel as
// multipart form data.
type RequestPrimitive = SerialisablePrimitive | File | FileList
export type RequestBody = {
  [key: string]:
    | RequestPrimitive
    | RequestBody
    | Array<RequestPrimitive | RequestBody>
}

// A resolved page. `name` is the key the response resolved to in the pages
// map (`search/classes/show`), stable across redirects, query strings, and
// re-renders on a POST URL, which is what a driver's scope is keyed on.
// `transient` is data true for this visit only, carried by the request that
// performed a change and read by the page it redirected to; an adapter fills
// it from whatever its host uses as the transport.
export type Page = {
  name: string
  baseUrl: URL
  node: React.ReactNode
  props: SerialisableBody
  transient: TransientData
}

// Transient data crosses the wire as JSON, so it is serialisable by
// construction; a page narrows it to the keys it expects.
export type TransientData = SerialisableBody

// Decides whether a driver renders a resolved page itself. A page the scope
// rejects is handed to the driver's host instead and never rendered here.
export type Scope = (page: Page) => boolean

// Receives a page a driver declined. The innermost driver whose scope accepts
// a page renders it; delegation only travels upward.
export type PageSink = (page: Page) => void

export enum VisitMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  PATCH = "patch",
  DELETE = "delete",
}

export type FetchOptions = {
  url: URL
  method: VisitMethod
  data: RequestBody
}

export type FetchPageOperation = {
  promise: Promise<Page>
  abort: () => void
  cancelled: boolean
}
export type FetchPage = (options: FetchOptions) => FetchPageOperation

export type ResponseAdapter = (options: {
  response: Response
  signal: AbortSignal
}) => Promise<Page>
export type RequestAdapter = (options: {
  url: URL
  method: VisitMethod
  data: RequestBody
  signal: AbortSignal
}) => Promise<Response>
